import { doc, getDoc } from 'firebase/firestore'
import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import type { Class, ClassroomCourse, ClassroomSyncResponse } from '@study-decks/shared'

import { useAuth } from '../../auth/AuthContext.js'
import { Button, Card } from '../../components'
import { showToast } from '../../components/Toast'
import { getDb } from '../../lib/firebase.js'
import { startClassroomConnect } from '../../lib/classroom-connect.js'
import {
  ClassroomApiError,
  listClassroomCourses,
  syncClassroomCourse,
} from '../../lib/classroom-api.js'
import styles from './ClassesPage.module.css'

type LoadState = 'loading' | 'not-connected' | 'connected' | 'error'

const DATE_FORMATTER = new Intl.DateTimeFormat(undefined, {
  dateStyle: 'medium',
  timeStyle: 'short',
})

function formatDate(value: string): string {
  if (!value) return 'Never'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? 'Never' : DATE_FORMATTER.format(date)
}

function stringList(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string')
    : []
}

function classFromData(id: string, data: Record<string, unknown>): Class {
  return {
    id,
    googleCourseId: typeof data.googleCourseId === 'string' ? data.googleCourseId : '',
    name: typeof data.name === 'string' ? data.name : '',
    section: typeof data.section === 'string' ? data.section : null,
    teacherIds: stringList(data.teacherIds),
    studentIds: stringList(data.studentIds),
    pendingInviteEmails: stringList(data.pendingInviteEmails),
    domain: typeof data.domain === 'string' ? data.domain : '',
    lastRosterSyncAt: typeof data.lastRosterSyncAt === 'string' ? data.lastRosterSyncAt : '',
    createdAt: typeof data.createdAt === 'string' ? data.createdAt : '',
    status: data.status === 'archived' ? 'archived' : 'active',
  }
}

export default function ClassesPage(): JSX.Element {
  const { firebaseUser } = useAuth()
  const [loadState, setLoadState] = useState<LoadState>('loading')
  const [courses, setCourses] = useState<ClassroomCourse[]>([])
  const [syncedClasses, setSyncedClasses] = useState<Record<string, Class>>({})
  const [syncResults, setSyncResults] = useState<Record<string, ClassroomSyncResponse>>({})
  const [syncingCourseId, setSyncingCourseId] = useState<string | null>(null)
  const [statusMessage, setStatusMessage] = useState('')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const loadCourses = useCallback(async () => {
    setLoadState('loading')
    setErrorMessage(null)
    try {
      const idToken = await firebaseUser?.getIdToken()
      if (!idToken) throw new Error('Sign in again to load Classroom courses.')
      const classroomCourses = await listClassroomCourses(idToken)
      setCourses(classroomCourses)
      const classDocs = await Promise.all(
        classroomCourses.map(async (course) => {
          const snapshot = await getDoc(doc(getDb(), 'classes', course.googleCourseId))
          return snapshot.exists()
            ? ([course.googleCourseId, classFromData(snapshot.id, snapshot.data())] as const)
            : null
        }),
      )
      setSyncedClasses(
        Object.fromEntries(
          classDocs.filter((item): item is NonNullable<typeof item> => item !== null),
        ),
      )
      setLoadState('connected')
    } catch (err) {
      if (err instanceof ClassroomApiError && err.code === 'CLASSROOM_RECONSENT_REQUIRED') {
        setLoadState('not-connected')
        return
      }
      const message = err instanceof Error ? err.message : 'Could not load Classroom courses.'
      setErrorMessage(message)
      setLoadState('error')
      showToast(message, 'error')
    }
  }, [firebaseUser])

  useEffect(() => {
    void loadCourses()
  }, [loadCourses])

  async function handleSync(course: ClassroomCourse) {
    try {
      const idToken = await firebaseUser?.getIdToken()
      if (!idToken) throw new Error('Sign in again to sync this roster.')
      setSyncingCourseId(course.googleCourseId)
      setStatusMessage(`Syncing ${course.name} roster…`)
      const result = await syncClassroomCourse(idToken, course.googleCourseId, {
        name: course.name,
        section: course.section,
      })
      setSyncResults((previous) => ({ ...previous, [course.googleCourseId]: result }))
      setSyncedClasses((previous) => ({ ...previous, [course.googleCourseId]: result.class }))
      setStatusMessage(
        `Synced ${course.name}: ${result.matchedStudents.length} matched, ${result.pendingInviteEmails.length} pending.`,
      )
      showToast('Roster synced.', 'success')
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not sync this roster.'
      setStatusMessage(message)
      showToast(message, 'error')
    } finally {
      setSyncingCourseId(null)
    }
  }

  return (
    <section className={styles.page} aria-labelledby="classes-title">
      <div className={styles.header}>
        <div>
          <p className={styles.eyebrow}>Google Classroom</p>
          <h2 id="classes-title">My classes</h2>
          <p className={styles.muted}>Sync read-only Classroom rosters into Study Decks classes.</p>
        </div>
        {loadState === 'connected' ? (
          <Button type="button" variant="secondary" onClick={() => void loadCourses()}>
            Refresh courses
          </Button>
        ) : null}
      </div>

      <p role="status" aria-live="polite" aria-atomic="true">
        {statusMessage}
      </p>

      {loadState === 'loading' ? (
        <p role="status" aria-live="polite">
          Loading Classroom courses…
        </p>
      ) : null}

      {loadState === 'not-connected' ? (
        <Card className={styles.connectCard} elevated>
          <h3>Connect Google Classroom</h3>
          <p>
            Study Decks will request read-only access to your active courses, rosters, and student
            emails so it can match students to class membership.
          </p>
          <Button type="button" onClick={() => void startClassroomConnect('/teacher/classes')}>
            Connect Google Classroom
          </Button>
        </Card>
      ) : null}

      {loadState === 'error' ? (
        <div className={styles.errorBlock}>
          <p role="alert">{errorMessage}</p>
          <Button type="button" variant="secondary" onClick={() => void loadCourses()}>
            Retry
          </Button>
        </div>
      ) : null}

      {loadState === 'connected' ? (
        courses.length === 0 ? (
          <div className={styles.emptyState}>
            <p>No active Classroom courses were found for this Google account.</p>
          </div>
        ) : (
          <Card className={styles.panel}>
            <div className={styles.sectionHeader}>
              <h3>Classroom courses</h3>
              <p className={styles.helpText}>
                Pending students get access automatically the first time they sign in.
              </p>
            </div>
            <div className={styles.tableWrap}>
              <table className={styles.courseTable}>
                <thead>
                  <tr>
                    <th scope="col">Course name</th>
                    <th scope="col">Section</th>
                    <th scope="col">Last synced</th>
                    <th scope="col">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {courses.map((course) => (
                    <tr key={course.googleCourseId}>
                      <td>
                        {syncedClasses[course.googleCourseId] ? (
                          <Link to={`/teacher/classes/${syncedClasses[course.googleCourseId]!.id}`}>
                            {course.name}
                          </Link>
                        ) : (
                          course.name
                        )}
                      </td>
                      <td>{course.section ?? 'No section'}</td>
                      <td>
                        {formatDate(syncedClasses[course.googleCourseId]?.lastRosterSyncAt ?? '')}
                      </td>
                      <td>
                        <Button
                          type="button"
                          size="sm"
                          loading={syncingCourseId === course.googleCourseId}
                          onClick={() => void handleSync(course)}
                        >
                          Sync roster
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )
      ) : null}

      {Object.entries(syncResults).map(([courseId, result]) => (
        <Card key={courseId} className={styles.resultPanel} highlighted>
          <h3>{result.class.name} sync result</h3>
          <div className={styles.resultColumns}>
            <div>
              <h4>Matched students</h4>
              {result.matchedStudents.length === 0 ? (
                <p className={styles.muted}>No existing Study Decks accounts matched.</p>
              ) : (
                <ul>
                  {result.matchedStudents.map((student) => (
                    <li key={student.uid}>
                      {student.displayName} — {student.email}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div>
              <h4>Pending invites</h4>
              {result.pendingInviteEmails.length === 0 ? (
                <p className={styles.muted}>No pending invites.</p>
              ) : (
                <ul>
                  {result.pendingInviteEmails.map((email) => (
                    <li key={email}>{email}</li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </Card>
      ))}
    </section>
  )
}
